import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from './api.js';
import type { User } from './types.js';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string, otp?: string, remember?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const refresh = async () => {
    try {
      const data = await api.get<{ user: User }>('/auth/me');
      setUser(data.user);
    } catch {
      setUser(null);
    }
  };
  useEffect(() => { void refresh().finally(() => setLoading(false)); }, []);
  const value = useMemo<AuthContextValue>(() => ({
    user,
    loading,
    login: async (email, password, otp, remember = false) => {
      const data = await api.post<{ user: User }>('/auth/login', { email, password, ...(otp ? { otp } : {}), remember });
      setUser(data.user);
    },
    logout: async () => { await api.post('/auth/logout'); setUser(null); },
    refresh,
  }), [user, loading]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
